"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const immutable_1 = require("immutable");
class Node {
    toString() {
        return this.value.toString();
    }
    evaluate() {
        return this;
    }
    equals(other) {
        return (other !== undefined &&
            this.constructor === other.constructor &&
            this.value === other.value);
    }
    rewrite(matches) {
        return this;
    }
    treeify(childPrefix, space) {
        return this.toString();
    }
    transform(defaultTransformation, transformationMap = immutable_1.Map()) {
        let transformed;
        defaultTransformation.rules.forEach(rule => {
            if (rule.lhs.equals(this)) {
                transformed = rule.rhs;
                return false;
            }
            const matches = rule.matches(this);
            if (!matches.isEmpty()) {
                transformed = rule.rhs.rewrite(matches).evaluate();
                return false;
            }
        });
        transformed = transformed || this;
        const recursivelyTransformed = transformed instanceof Operator
            ? transformed
                .setChildren(transformed.children.map(child => child.transform(defaultTransformation, transformationMap)))
                .evaluate()
            : transformed;
        return this.equals(recursivelyTransformed)
            ? recursivelyTransformed
            : recursivelyTransformed.transform(defaultTransformation, transformationMap);
    }
    execute(namespace) {
        const evaluated = this.evaluate();
        return {
            namespace: Object.assign({}, namespace, { expression: evaluated }),
            stdOut: evaluated.toString(),
        };
    }
}
exports.default = Node;
class Num extends Node {
    constructor(value) {
        super();
        this.value = value;
    }
    evaluate() {
        return this.value < 0
            ? new Operator(OperatorSymbol.Minus, immutable_1.List([new Num(this.value * -1)]))
            : super.evaluate();
    }
}
exports.Num = Num;
var ExpressionSymbol;
(function (ExpressionSymbol) {
    ExpressionSymbol["LeftParens"] = "(";
    ExpressionSymbol["RightParens"] = ")";
    ExpressionSymbol["Comma"] = ",";
    ExpressionSymbol["None"] = "";
})(ExpressionSymbol = exports.ExpressionSymbol || (exports.ExpressionSymbol = {}));
var OperatorSymbol;
(function (OperatorSymbol) {
    OperatorSymbol["Plus"] = "+";
    OperatorSymbol["Minus"] = "-";
    OperatorSymbol["Mul"] = "*";
    OperatorSymbol["Div"] = "/";
    OperatorSymbol["Pow"] = "^";
    OperatorSymbol["And"] = "and";
    OperatorSymbol["Or"] = "or";
    OperatorSymbol["Equals"] = "==";
    OperatorSymbol["Flag"] = "is";
    OperatorSymbol["Not"] = "not";
    OperatorSymbol["Depends"] = "depends";
    OperatorSymbol["Constant"] = "const";
})(OperatorSymbol = exports.OperatorSymbol || (exports.OperatorSymbol = {}));
class Operator extends Node {
    constructor(value, children = immutable_1.List(), operatorHandlers = operatorSymbolHandlers) {
        super();
        this.value = value;
        this.children = children;
        const notFoundHandlers = {
            evaluator: {
                f: (children) => new Operator(this.value, children),
                recursive: false,
            },
            commutative: false,
        };
        const handlers = operatorHandlers.get(this.value, notFoundHandlers);
        this.evaluator = handlers.evaluator;
        this.commutative = handlers.commutative;
        this.stringifier = handlers.stringifier;
    }
    setChildren(childen) {
        return new Operator(this.value, childen);
    }
    addChild(child) {
        return new Operator(this.value, this.children.push(child));
    }
    toString() {
        const stringifier = this.stringifier;
        if (!stringifier) {
            return prefix(this.value, this.children.map(child => child.toString()));
        }
        const stringifiedChildren = this.children.map((child, index) => child instanceof Operator ? stringifier(child, index, this) : child.toString());
        return infix(this.value, stringifiedChildren);
    }
    treeify(childPrefix, space) {
        return this.children.reduce((treeified, child, index, children) => {
            const [branch, prefixExtention] = index === children.size - 1 ? ['└─', space] : ['├─', '│'];
            return `${treeified}\n${childPrefix}${branch} ${child.treeify(`${childPrefix}${prefixExtention}${space}${space}`, space)}`;
        }, this.value);
    }
    transform(defaultTransformation, transformationMap = immutable_1.Map()) {
        const transformation = transformationMap.get(this.value, defaultTransformation);
        return super.transform(transformation, transformationMap);
    }
    evaluate() {
        const evaluatedChildren = this.evaluator.recursive
            ? this.children
            : this.children.map((child) => {
                return child instanceof Operator ? child.evaluate() : child;
            });
        return this.evaluator.f(evaluatedChildren, this.commutative);
    }
    equals(other) {
        return (super.equals(other) &&
            this.children.size === other.children.size &&
            this.children.every((child, index) => child.equals(other.children.get(index))));
    }
    rewrite(matches) {
        return this.setChildren(this.children.map(child => child.rewrite(matches)));
    }
}
exports.Operator = Operator;
class Symbol extends Node {
    constructor(value) {
        super();
        this.value = value;
    }
}
exports.Symbol = Symbol;
class Rewritable extends Node {
    constructor(value) {
        super();
        this.value = value;
    }
    rewrite(matches) {
        return matches.get(this.toString(), new Rewritable(this.value));
    }
    toString() {
        return `$${super.toString()}`;
    }
}
exports.Rewritable = Rewritable;
const operatorSymbolHandlers = immutable_1.Map([
    [
        OperatorSymbol.Plus,
        {
            evaluator: {
                f: evaluateNumericalOperator(OperatorSymbol.Plus, (left, right) => left + right),
                recursive: false,
            },
            commutative: true,
            stringifier: stringifyChildPlus,
        },
    ],
    [
        OperatorSymbol.Minus,
        {
            evaluator: {
                f: evaluateNumericalOperator(OperatorSymbol.Minus, (left, right) => left - right),
                recursive: false,
            },
            commutative: false,
            stringifier: stringifyChildMinus,
        },
    ],
    [
        OperatorSymbol.Mul,
        {
            evaluator: {
                f: evaluateNumericalOperator(OperatorSymbol.Mul, (left, right) => left * right),
                recursive: false,
            },
            commutative: true,
            stringifier: stringifyChild(immutable_1.List([OperatorSymbol.Plus, OperatorSymbol.Minus])),
        },
    ],
    [
        OperatorSymbol.Div,
        {
            evaluator: {
                f: evaluateNumericalOperator(OperatorSymbol.Div, (left, right) => left / right),
                recursive: false,
            },
            commutative: false,
            stringifier: stringifyChild(immutable_1.List([OperatorSymbol.Plus, OperatorSymbol.Minus])),
        },
    ],
    [
        OperatorSymbol.Pow,
        {
            evaluator: {
                f: evaluateNumericalOperator(OperatorSymbol.Pow, (left, right) => left ** right),
                recursive: false,
            },
            commutative: false,
            stringifier: stringifyChild(immutable_1.List([
                OperatorSymbol.Plus,
                OperatorSymbol.Minus,
                OperatorSymbol.Mul,
                OperatorSymbol.Div,
            ])),
        },
    ],
    [
        OperatorSymbol.Depends,
        {
            evaluator: { f: evaluateDepends, recursive: true },
            commutative: false,
        },
    ],
    [
        OperatorSymbol.And,
        {
            evaluator: {
                f: (children, _) => children.every(child => child.evaluate().equals(exports.TRUE)) ? exports.TRUE : exports.FALSE,
                recursive: false,
            },
            commutative: false,
        },
    ],
    [
        OperatorSymbol.Or,
        {
            evaluator: {
                f: (children, _) => children.some(child => child.evaluate().equals(exports.TRUE)) ? exports.TRUE : exports.FALSE,
                recursive: false,
            },
            commutative: false,
        },
    ],
    [
        OperatorSymbol.Not,
        {
            evaluator: {
                f: (children) => children
                    .first()
                    .evaluate()
                    .equals(exports.TRUE)
                    ? exports.FALSE
                    : exports.TRUE,
                recursive: false,
            },
            commutative: false,
        },
    ],
    [
        OperatorSymbol.Flag,
        {
            evaluator: {
                f: (children, _) => children.first().evaluate(),
                recursive: false,
            },
            commutative: false,
        },
    ],
    [
        OperatorSymbol.Equals,
        {
            evaluator: {
                f: (children, _) => children.every(child => child.equals(children.first())) ? exports.TRUE : exports.FALSE,
                recursive: false,
            },
            commutative: true,
            stringifier: (child, index) => child.toString(),
        },
    ],
    [
        OperatorSymbol.Constant,
        {
            evaluator: {
                f: (children, _) => {
                    const evaluatedChild = children.first().evaluate();
                    return evaluatedChild instanceof Num || evaluatedChild instanceof Symbol
                        ? exports.TRUE
                        : exports.FALSE;
                },
                recursive: false,
            },
            commutative: false,
        },
    ],
]);
exports.TRUE = new Num(1);
exports.FALSE = new Num(0);
function evaluateNumericalOperator(operatorSymbol, operation) {
    return (children, commutative) => {
        if (children.size < 2) {
            return new Operator(operatorSymbol, children);
        }
        const reducedChildren = children.reduce((reducedChildren, child) => {
            const last = reducedChildren.last();
            if (last instanceof Num && child instanceof Num) {
                return reducedChildren
                    .butLast()
                    .push(new Num(operation(last.value, child.value)));
            }
            const numComparator = (previous, next) => {
                if (previous instanceof Num) {
                    if (next instanceof Num) {
                        return 0;
                    }
                    return 1;
                }
                if (next instanceof Num) {
                    return -1;
                }
                return 0;
            };
            return commutative
                ? reducedChildren.push(child).sort(numComparator)
                : reducedChildren.push(child);
        }, immutable_1.List());
        const first = reducedChildren.first();
        return reducedChildren.size === 1
            ? first.evaluate()
            : new Operator(operatorSymbol, reducedChildren);
    };
}
function evaluateDepends(children, commutative = false) {
    const dependency = children.last();
    if (dependency instanceof Num) {
        throw Error(`No expression can depend on ${dependency}`);
    }
    const dependents = children.butLast();
    if (dependents.size === 1) {
        const dependent = dependents.first();
        if (dependent.equals(dependency)) {
            return exports.TRUE;
        }
        if (dependent instanceof Operator) {
            if (dependent.children.some(child => evaluateDepends(immutable_1.List([child, dependency])).equals(exports.TRUE))) {
                return exports.TRUE;
            }
        }
        return exports.FALSE;
    }
    return dependents.every(dependent => evaluateDepends(immutable_1.List([dependent, dependency])).equals(exports.TRUE))
        ? exports.TRUE
        : exports.FALSE;
}
function infix(operatorSymbol, children) {
    return children.size > 1
        ? children.join(operatorSymbol)
        : `${operatorSymbol}${children.first()}`;
}
function prefix(operatorSymbol, children) {
    return [
        operatorSymbol,
        ExpressionSymbol.LeftParens,
        children.join(children.size > 1 ? ExpressionSymbol.Comma : ExpressionSymbol.None),
        ExpressionSymbol.RightParens,
    ].join(ExpressionSymbol.None);
}
function parenthesize(child) {
    return [ExpressionSymbol.LeftParens, child, ExpressionSymbol.RightParens].join(ExpressionSymbol.None);
}
function stringifyChildPlus(child, index, _) {
    return index !== 0 && child.value === OperatorSymbol.Minus && child.children.size === 1
        ? parenthesize(child)
        : child.toString();
}
function stringifyChildMinus(child, index, parent) {
    const startIdx = parent.children.size === 1 ? 0 : 1;
    return index >= startIdx &&
        immutable_1.List([OperatorSymbol.Plus, OperatorSymbol.Minus]).includes(child.value)
        ? parenthesize(child)
        : child.toString();
}
function stringifyChild(parenthesized) {
    return (child, ..._) => parenthesized.includes(child.value)
        ? parenthesize(child)
        : child.toString();
}
//# sourceMappingURL=Node.js.map